import { Yap, ShoutedYap } from '../../types';
import { getUserAvatar, isGhostAvatar } from '../../utils/userUtils';

/** A denormalized avatar value, or '' when it is a placeholder. */
const nonGhost = (v?: string | null): string => (v && !isGhostAvatar(v) ? v : '');

export interface YapAuthorInfo {
  name: string;
  username: string;
  avatar: string;
  country: string;
  location?: string;
  residency?: string;
  rank?: string;
}

/**
 * Standardizes author info extraction from both regular Yaps and ShoutedYaps.
 */
export function getYapAuthorInfo(yap: any, currentUserProfile?: any): YapAuthorInfo {
  // Helper to ensure we don't confuse location text with country name
  const getProperLocation = (res: string | undefined, loc: string | undefined, country: string | undefined): string => {
    if (res && res.trim().length > 1) return res;
    if (loc && loc.trim().length > 1) return loc;
    if (country && country.trim().length > 1) return country;
    return 'Earth';
  };

  // Helper for STRICT residency-only (no country fallback)
  const getResidencyOnly = (res: string | undefined, loc: string | undefined): string | undefined => {
    if (res && res.trim().length > 1) return res;
    if (loc && loc.trim().length > 1) return loc;
    return undefined;
  };

  // 🛡️ CRITICAL FIX: Use the provided profile (live) if it matches the yap author
  const authorId = yap.userId || yap.authorId || yap.shoutedBy;
  const isTargetAuthor = currentUserProfile && (
    (authorId && String(authorId) === String(currentUserProfile.id)) ||
    (yap.username && String(yap.username).replace('@', '') === String(currentUserProfile.username).replace('@', '')) ||
    (yap.shouterUsername && String(yap.shouterUsername).replace('@', '') === String(currentUserProfile.username).replace('@', ''))
  );

  const authorObj = isTargetAuthor ? currentUserProfile : (typeof yap.author === 'object' ? yap.author : null);

  // Check if it's a ShoutedYap structure
  if (yap.shoutedByName || (yap.type === 'shouted')) {
    const showLoc = yap.shoutedByShowLocation !== false;
    return {
      name: yap.shoutedByName || authorObj?.name || 'User',
      username: yap.shoutedByUsername || authorObj?.username || '@user',
      // The denormalized copies are filtered too: a ghost placeholder written at
      // signup gets copied onto posts by syncProfileToPosts, so the post's own
      // avatar field can carry one long after the profile stops showing it.
      avatar: nonGhost(yap.shoutedByAvatar) || (authorObj ? getUserAvatar(authorObj) : '') || '',
      country: yap.shoutedByCountryFlag || authorObj?.country || yap.shoutedByCountry || '',
      location: showLoc
        ? getProperLocation(authorObj?.home_residence || yap.shoutedByResidence, authorObj?.location || yap.shoutedByLocation, yap.shoutedByCountryFlag || yap.shoutedByCountry)
        : 'Earth',
      residency: showLoc ? getResidencyOnly(authorObj?.home_residence || yap.shoutedByResidence, authorObj?.location || yap.shoutedByLocation) : undefined,
      rank: (authorObj?.rank || yap.rank)
    };
  }

  const showLoc = yap.showLocation !== false;
  return {
    name: authorObj?.name || yap.author || 'User',
    username: authorObj?.username || yap.username || '@user',
    avatar: (authorObj ? getUserAvatar(authorObj) : '') || nonGhost(yap.avatar) || '',
    country: yap.authorCountryFlag || authorObj?.country || yap.country || yap.userCountry || '',
    location: showLoc
      ? getProperLocation(authorObj?.home_residence || yap.home_residence, authorObj?.location || yap.location, authorObj?.country || yap.country || yap.authorCountryFlag)
      : 'Earth',
    residency: showLoc ? getResidencyOnly(authorObj?.home_residence || yap.home_residence, authorObj?.location || yap.location) : undefined,
    rank: (authorObj?.rank || yap.rank)
  };
}

/**
 * Ensures we use the correct ID for interactions (likes/shouts).
 * For a ShoutedYap, we usually want to interact with the original Yap.
 */
/**
 * Returns the correct ID for interactions (likes, replies).
 * Simple reposts (shouts without caption/images) target the original yap.
 * Quote shouts and regular Yaps target themselves.
 */
export function getYapInteractionId(yap: any): string {
  if (!yap) return '';
  
  // 🔄 REPOST LOGIC: Simple reposts (no caption/images/audio) target the original content
  if (isShoutedYap(yap) && !isQuoteShout(yap)) {
    const originalId = yap.originalYapId || yap.originalYap?.id;
    if (originalId) return String(originalId);
  }
  
  // 📢 QUOTE SHOUT / REGULAR YAP: Target itself
  const id = yap.id || yap._id || yap.yapId;
  return id ? String(id) : '';
}

/**
 * Returns the interaction post type for a given Yap/Shout.
 * Quote shouts are 'shout' entity types. Simple reposts and regular yaps are 'yap' types.
 */
export function getYapPostType(yap: any): 'yap' | 'shout' {
  // If it's a shout but NOT a quote (simple repost), it interacts as the original 'yap'
  if (isShoutedYap(yap) && !isQuoteShout(yap)) return 'yap';
  
  // If it's a quote shout, it's a 'shout' type interaction
  if (isQuoteShout(yap)) return 'shout';
  
  return 'yap';
}

/**
 * Returns the original Yap ID if the post is a shout.
 * Always targets the root content for shouting/sharing.
 */
export function getOriginalYapId(yap: any): string {
  if (!yap) return '';
  const originalId = yap.originalYapId || yap.originalYap?.id;
  if (originalId) return String(originalId);
  return getYapInteractionId(yap);
}

/**
 * Helper to check if an object is a ShoutedYap.
 */
export function isShoutedYap(yap: any): boolean {
  return !!(yap.shoutedByUsername || yap.type === 'shouted');
}

/**
 * Helper to check if it's a quote shout (has caption or images) vs a simple shout (repost).
 */
export function isQuoteShout(yap: any): boolean {
  if (!isShoutedYap(yap)) return false;
  return !!(yap.shoutCaption || (yap.shoutImages && yap.shoutImages.length > 0) || yap.audioUrl);
}

/**
 * Helper to check if a URL is likely a video based on extension or path.
 */
export function isMaybeVideo(url?: string): boolean {
  if (!url) return false;
  const clean = url.toLowerCase().split('?')[0];
  if (clean.endsWith('.mp4') || clean.endsWith('.mov') || clean.endsWith('.webm') || clean.endsWith('.m4v')) return true;
  if (url.includes('video/')) return true;
  return false;
}
